'use client';

import {
  Artifact,
  ArtifactActions,
  ArtifactClose,
  ArtifactContent,
  ArtifactHeader,
  ArtifactTitle,
} from '@/components/ai-elements/artifact';
import { ShareButton, type ShareMediaItem } from '@/components/share-button';
import { extractPrimaryMediaItem } from '@/components/media-artifact-renderer';
import {
  getVisualizationDisplayTitle,
  ToolVisualizationRenderer,
} from '@/components/tool-visualization-renderer';
import { getVisualizationTypeLabel } from '@/lib/visualization-type-labels';
import { getToolTheme } from '@/lib/tool-display';
import {
  FlowArrow, Clock, Table, TreeStructure, Graph, Atom, Play,
  FilmSlate, Cube, ImageSquare, ChartLineUp, Globe, Eye,
  SpeakerHigh, MusicNote, WaveTriangle, TerminalWindow, FileCode, BookOpen,
} from '@phosphor-icons/react';

const BROWSER_ARTIFACT_TOOLS = new Set([
  'createBrowser',
  'browserLiveView',
  'browserNavigate',
  'browserScroll',
]);

const SANDBOX_PREVIEW_ARTIFACT_TOOLS = new Set([
  'sandboxPreviewUrl',
]);

const FULL_BLEED_ARTIFACT_TOOLS = new Set([
  ...BROWSER_ARTIFACT_TOOLS,
  ...SANDBOX_PREVIEW_ARTIFACT_TOOLS,
]);

// Per-theme accents for the header chip so the panel reflects the tool
// family at a glance (browser=sky, sandbox=emerald, media=pink, …) instead
// of everything reading as the same violet card.
const THEME_ACCENTS: Record<string, { chip: string; icon: string; dot: string }> = {
  violet: { chip: 'border-violet-500/25 bg-violet-500/[0.06]', icon: 'text-violet-300', dot: 'bg-violet-400' },
  cyan: { chip: 'border-cyan-500/25 bg-cyan-500/[0.06]', icon: 'text-cyan-300', dot: 'bg-cyan-400' },
  amber: { chip: 'border-amber-500/25 bg-amber-500/[0.06]', icon: 'text-amber-300', dot: 'bg-amber-400' },
  pink: { chip: 'border-pink-500/25 bg-pink-500/[0.06]', icon: 'text-pink-300', dot: 'bg-pink-400' },
  emerald: { chip: 'border-emerald-500/25 bg-emerald-500/[0.06]', icon: 'text-emerald-300', dot: 'bg-emerald-400' },
  sky: { chip: 'border-sky-500/25 bg-sky-500/[0.06]', icon: 'text-sky-300', dot: 'bg-sky-400' },
  indigo: { chip: 'border-indigo-500/25 bg-indigo-500/[0.06]', icon: 'text-indigo-300', dot: 'bg-indigo-400' },
};

export function getArtifactAccent(toolName: string) {
  return THEME_ACCENTS[getToolTheme(toolName)] ?? THEME_ACCENTS.violet;
}

export function ArtifactTypeIcon({ toolName, iconClass }: { toolName: string; iconClass: string }) {
  return <TypeIcon toolName={toolName} iconClass={iconClass} />;
}

function TypeIcon({ toolName, iconClass }: { toolName: string; iconClass: string }) {
  switch (toolName) {
    case 'flowchart': return <FlowArrow weight="duotone" className={iconClass} />;
    case 'timeline': return <Clock weight="duotone" className={iconClass} />;
    case 'comparisonTable': case 'comparison-table': return <Table weight="duotone" className={iconClass} />;
    case 'conceptMap': case 'concept-map': return <TreeStructure weight="duotone" className={iconClass} />;
    case 'cycleDiagram': case 'cycle-diagram': return <ChartLineUp weight="duotone" className={iconClass} />;
    case 'hierarchy': case 'graph': return <Graph weight="duotone" className={iconClass} />;
    case 'molecule3d': return <Atom weight="duotone" className={iconClass} />;
    case 'p5': return <Play weight="duotone" className={iconClass} />;
    case 'remotion':
    case 'hyperframes':
    case 'createSceneVideo':
      return <FilmSlate weight="duotone" className={iconClass} />;
    case 'scene3d': return <Cube weight="duotone" className={iconClass} />;
    case 'generateImage':
    case 'generateImageBatch':
    case 'viewMediaAsset':
    case 'saveMediaAssetFromUrl':
    case 'promoteGeneratedMediaToAsset':
      return <ImageSquare weight="duotone" className={iconClass} />;
    case 'generateSpeech':
    case 'generateNarrationPlan':
      return <SpeakerHigh weight="duotone" className={iconClass} />;
    case 'generateMusic':
      return <MusicNote weight="duotone" className={iconClass} />;
    case 'generateSoundEffect':
    case 'generateSoundEffectBatch':
      return <WaveTriangle weight="duotone" className={iconClass} />;
    case 'sandboxExec':
    case 'sandboxCodeRun':
    case 'sandboxSessionExec':
    case 'sandboxSessionLogs':
      return <TerminalWindow weight="duotone" className={iconClass} />;
    case 'sandboxListFiles':
    case 'sandboxReadFile':
    case 'browserListFiles':
    case 'browserReadFile':
    case 'readGalexiaFile':
    case 'readGalexiaFileLines':
    case 'getVisualizationSource':
    case 'readVisualizationLines':
    case 'grepVisualization':
      return <FileCode weight="duotone" className={iconClass} />;
    case 'listMyDocuments':
    case 'viewDocument':
      return <BookOpen weight="duotone" className={iconClass} />;
    case 'createBrowser':
    case 'browserLiveView':
    case 'browserNavigate':
    case 'browserScroll':
      return <Globe weight="duotone" className={iconClass} />;
    case 'sandboxPreviewUrl':
      return <Eye weight="duotone" className={iconClass} />;
    case 'createNarratedVideo':
    case 'createParallelNarratedVideo':
      return <FilmSlate weight="duotone" className={iconClass} />;
    default: return <FlowArrow weight="duotone" className={iconClass} />;
  }
}

interface VisualizationArtifactPanelProps {
  toolName: string;
  input: Record<string, unknown>;
  output?: unknown;
  visualizationId?: string;
  sessionId?: string | null;
  onClose: () => void;
  onRetryError?: (info: { toolName: string; error: string; code: string }) => void;
}

export function VisualizationArtifactPanel({
  toolName,
  input,
  output,
  visualizationId,
  sessionId,
  onClose,
  onRetryError,
}: VisualizationArtifactPanelProps) {
  const title = getVisualizationDisplayTitle(toolName, input);
  const useIntegratedHeader = toolName === 'html' || toolName === 'svg' || toolName === 'mermaid';
  const isFullBleedArtifact = FULL_BLEED_ARTIFACT_TOOLS.has(toolName);
  const typeLabel = getVisualizationTypeLabel(toolName);
  const accent = getArtifactAccent(toolName);

  // Raw tool-output media (a generated video/image/audio clip with no
  // visualization row) → shareable via the shared_artifacts /x/:slug flow.
  // Only consulted when there's no visualizationId (those keep the /v flow).
  const mediaItem: ShareMediaItem | null = visualizationId
    ? null
    : (() => {
        const item = extractPrimaryMediaItem(output, input);
        return item ? { ...item, toolName } : null;
      })();

  return (
    <Artifact className="h-full rounded-none border-0 shadow-none animate-in fade-in-0 slide-in-from-right-4 duration-300">
      {!useIntegratedHeader && (
        <ArtifactHeader className="border-border/60 bg-background/80 px-4 py-2.5 backdrop-blur-md supports-[backdrop-filter]:bg-background/70">
          <div className="min-w-0 flex items-center gap-3">
            {!isFullBleedArtifact && (
              <div className={`shrink-0 inline-flex size-9 items-center justify-center rounded-xl border ${accent.chip}`}>
                <TypeIcon toolName={toolName} iconClass={`size-4.5 ${accent.icon}`} />
              </div>
            )}
            <div className="min-w-0">
              <ArtifactTitle className="truncate font-medium leading-tight">{title}</ArtifactTitle>
              {!isFullBleedArtifact && (
                <div className="mt-1 inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground/60">
                  <span className={`size-1.5 rounded-full ${accent.dot}`} aria-hidden />
                  {typeLabel}
                </div>
              )}
            </div>
          </div>
          <ArtifactActions>
            {!isFullBleedArtifact && (
              <ShareButton
                visualizationId={visualizationId}
                sessionId={sessionId}
                type={toolName}
                title={title}
                mediaItem={mediaItem}
              />
            )}
            <ArtifactClose
              onClick={onClose}
              className="size-8 rounded-full border border-border/70 bg-background/40 hover:bg-muted/60"
            />
          </ArtifactActions>
        </ArtifactHeader>
      )}

      <ArtifactContent className={isFullBleedArtifact ? 'flex min-h-0 w-full flex-1 overflow-hidden p-0' : 'min-h-0 p-0'}>
        <div className={isFullBleedArtifact ? 'h-full min-h-0 w-full flex-1 overflow-hidden p-0' : 'h-full overflow-auto p-4'}>
          <ToolVisualizationRenderer
            toolName={toolName}
            input={input}
            output={output}
            visualizationId={visualizationId}
            sessionId={sessionId}
            onRetryError={onRetryError}
            displayContext="artifact"
            renderActions={useIntegratedHeader ? <ArtifactClose onClick={onClose} /> : undefined}
          />
        </div>
      </ArtifactContent>
    </Artifact>
  );
}
